import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ChevronDown, Menu, X } from 'lucide-react';
import { getLoginUrl, logout, checkAuth } from '../services/api';

export const Header = ({ user: propUser, onUserChange }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(propUser);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Sync with prop user when it changes
  useEffect(() => {
    setUser(propUser);
  }, [propUser]);

  // If no prop user provided, check auth on mount (backward compatibility)
  useEffect(() => {
    if (propUser === undefined) {
      checkAuth()
        .then(userData => setUser(userData))
        .catch(() => setUser(null));
    }
  }, [propUser]);

  const handleLogin = () => {
    window.location.href = getLoginUrl();
  };

  const handleLogout = () => {
    logout();
    if (onUserChange) onUserChange(null);
  };

  const handleNavClick = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-4 md:top-6 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-7xl">
      <div className="backdrop-blur-md bg-black/30 border border-white/10 rounded-full px-4 md:px-8 py-3 md:py-4 flex items-center justify-between shadow-lg shadow-purple-500/10">
        {/* Logo */}
        <div className="flex items-center cursor-pointer" onClick={() => handleNavClick('/')}>
          <img 
            src="https://customer-assets.emergentagent.com/job_551626b0-f9c7-4868-8db1-d6bfdfbb1a20/artifacts/uqdo6djg_Copilot_20260203_160408.png" 
            alt="Shadingo Rewards" 
            className="h-8 md:h-12 w-auto"
          />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <Link 
            to="/" 
            className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium hover:scale-105"
          >
            Home
          </Link>
          <Link 
            to="/leaderboard" 
            className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium hover:scale-105"
          >
            Leaderboard
          </Link>
          
          {/* More Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium hover:scale-105 flex items-center gap-1">
                More <ChevronDown className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-black/90 border-purple-500/30">
              <DropdownMenuItem 
                onClick={() => navigate('/bonus-hunt')}
                className="text-white hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer"
              >
                Bonus Hunt
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => navigate('/guessing')}
                className="text-white hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer"
              >
                End Balance Guessing
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Admin Panel Link (only for admins) */}
          {user?.is_admin && (
            <Link 
              to="/admin" 
              className="text-yellow-400 hover:text-yellow-300 transition-all duration-300 text-lg font-exo font-medium hover:scale-105"
            >
              Admin Panel
            </Link>
          )}
        </nav>

        {/* Desktop Login/Profile */}
        <div className="hidden md:block">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2">
                  <Avatar className="h-10 w-10 border-2 border-purple-500/50">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback className="bg-purple-500 text-black">{user.discord_username[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-black/90 border-purple-500/30">
                <DropdownMenuItem 
                  onClick={() => navigate('/account-settings')}
                  className="text-white hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                >
                  Account Settings
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-white hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={handleLogin}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white px-6 py-3 rounded-full font-orbitron font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/50 border-0 hover:scale-105"
            >
              Login via Discord
            </Button>
          )}
        </div>

        {/* Mobile Login/Avatar - Always visible on mobile */}
        <div className="flex md:hidden items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center">
                  <Avatar className="h-9 w-9 border-2 border-purple-500/50">
                    <AvatarImage src={user.avatar} />
                    <AvatarFallback className="bg-purple-500 text-black text-sm">{user.discord_username[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-black/90 border-purple-500/30">
                <DropdownMenuItem 
                  onClick={() => { navigate('/account-settings'); setMobileMenuOpen(false); }}
                  className="text-white hover:text-purple-400 hover:bg-purple-500/10 cursor-pointer"
                >
                  Account Settings
                </DropdownMenuItem>
                {user?.is_admin && (
                  <DropdownMenuItem 
                    onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                    className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10 cursor-pointer"
                  >
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                >
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button 
              onClick={handleLogin}
              size="sm"
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white px-3 py-2 rounded-full font-orbitron font-semibold text-xs transition-all duration-300 border-0"
            >
              Login
            </Button>
          )}
          
          {/* Mobile Menu Button */}
          <button 
            className="text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden mt-2 backdrop-blur-md bg-black/90 border border-white/10 rounded-2xl px-4 py-4 shadow-lg shadow-purple-500/10">
          <nav className="flex flex-col gap-4">
            <button 
              onClick={() => handleNavClick('/')}
              className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium text-left py-2 border-b border-white/10"
            >
              Home
            </button>
            <button 
              onClick={() => handleNavClick('/leaderboard')}
              className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium text-left py-2 border-b border-white/10"
            >
              Leaderboard
            </button>
            <button 
              onClick={() => handleNavClick('/bonus-hunt')}
              className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium text-left py-2 border-b border-white/10"
            >
              Bonus Hunt
            </button>
            <button 
              onClick={() => handleNavClick('/guessing')}
              className="text-white/90 hover:text-purple-400 transition-all duration-300 text-lg font-exo font-medium text-left py-2 border-b border-white/10"
            >
              End Balance Guessing
            </button>
            
            {user?.is_admin && (
              <button 
                onClick={() => handleNavClick('/admin')}
                className="text-yellow-400 hover:text-yellow-300 transition-all duration-300 text-lg font-exo font-medium text-left py-2"
              >
                Admin Panel
              </button>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
